import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, UIEvent } from 'react';
import { Avatar, Textarea, cn } from '@vd/ui';
import type { TextareaProps } from '@vd/ui';
import type { User } from '@vd/types';
import { renderMentions } from './renderMentions';
import styles from './MentionTextarea.module.css';

// Detects whether the cursor sits inside a partial @-token. Mirrors the
// server-side parser's name characters but doesn't require the trailing
// boundary char (the user is mid-typing, after all).
const MENTION_PARTIAL_RE = /(?:^|[^\w@])@([A-Za-z][\w'\- ]*)?$/;

// Aliases the autocomplete accepts as shortcuts to the current user.
// Resolution still inserts the user's real display name, so the server
// parser doesn't need to know about "me" — it's a UX shortcut only.
const SELF_ALIASES = ['me', 'myself'];

export interface MentionTextareaProps
  extends Omit<TextareaProps, 'value' | 'onChange'> {
  value: string;
  onChange: (next: string) => void;
  members: User[];
  /**
   * The current user's id. When provided, that member shows up in the
   * autocomplete with a "(me)" suffix and can also be matched by typing
   * `@me` / `@myself`.
   */
  currentUserId?: string;
}

/**
 * Textarea with two enhancements over `<Textarea>`:
 *
 *   1. @-mention autocomplete. Typing `@` followed by name characters
 *      pops up a member list; selection inserts `@<full name> ` so the
 *      server-side parser resolves it to a user id.
 *   2. Live highlight overlay. A mirror `<div>` sits behind the
 *      textarea with identical font + padding metrics; rendering goes
 *      through `renderMentions` so resolved @-tokens get a colored
 *      pill behind them. The textarea itself stays a normal textarea
 *      so input / selection / paste all work natively.
 *
 * The overlay sync model: the textarea is the source of truth for
 * scroll + caret. On every scroll event we propagate scrollTop/Left
 * to the overlay so the highlight rectangles track the visible text.
 */
export const MentionTextarea = forwardRef<HTMLTextAreaElement, MentionTextareaProps>(
  function MentionTextarea(
    { value, onChange, members, currentUserId, onKeyDown, onBlur, onScroll, className, ...rest },
    ref,
  ) {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const overlayRef = useRef<HTMLDivElement | null>(null);
    useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement, []);

    const [activeIndex, setActiveIndex] = useState(0);
    const [open, setOpen] = useState(false);

    // Sync the overlay's padding + border + font from the textarea's
    // resolved styles. The overlay can't just wear the same className
    // because the `<Textarea>` primitive layers its own `.input` class
    // on top (which adds padding + border). Without this sync, the
    // overlay's content starts at the wrapper's edge while the
    // textarea's content starts inside its border + padding — and a
    // mention pill drawn at the wrong x/y is invisible to the user
    // (often clipped under the textarea's border).
    //
    // Re-runs when className changes (caller swapped a class) and on
    // mount. Font metrics rarely change after that, so we don't bother
    // with ResizeObserver / MutationObserver here.
    useEffect(() => {
      const ta = textareaRef.current;
      const overlay = overlayRef.current;
      if (!ta || !overlay) return;
      const cs = window.getComputedStyle(ta);
      overlay.style.paddingTop = cs.paddingTop;
      overlay.style.paddingRight = cs.paddingRight;
      overlay.style.paddingBottom = cs.paddingBottom;
      overlay.style.paddingLeft = cs.paddingLeft;
      overlay.style.borderTopWidth = cs.borderTopWidth;
      overlay.style.borderRightWidth = cs.borderRightWidth;
      overlay.style.borderBottomWidth = cs.borderBottomWidth;
      overlay.style.borderLeftWidth = cs.borderLeftWidth;
      overlay.style.borderStyle = 'solid';
      overlay.style.fontFamily = cs.fontFamily;
      overlay.style.fontSize = cs.fontSize;
      overlay.style.fontWeight = cs.fontWeight;
      overlay.style.lineHeight = cs.lineHeight;
      overlay.style.letterSpacing = cs.letterSpacing;
      overlay.style.tabSize = cs.tabSize;
      overlay.style.boxSizing = cs.boxSizing;
    }, [className]);

    // Detect whether the cursor is currently in an @-token. Returns the
    // partial query (text after the @) or null if no active token.
    const detect = (): { query: string; tokenStart: number; tokenEnd: number } | null => {
      const ta = textareaRef.current;
      if (!ta) return null;
      const caret = ta.selectionStart ?? value.length;
      // Only tokenize if selectionStart === selectionEnd (no range). A
      // user with a selection is editing, not typing — don't pop up.
      if (ta.selectionEnd !== caret) return null;
      const upToCaret = value.slice(0, caret);
      const m = upToCaret.match(MENTION_PARTIAL_RE);
      if (!m) return null;
      const query = m[1] ?? '';
      // tokenStart is the index of the '@' itself.
      const tokenStart = caret - query.length - 1;
      return { query, tokenStart, tokenEnd: caret };
    };

    const detection = detect();

    // Filtered + ordered candidate list. Match rule:
    //   - Empty query (just "@"): show everyone, current user first.
    //   - "me"/"myself" prefix: surface the current user at the top.
    //   - Otherwise: case-insensitive name prefix + substring fallback.
    // Trimmed to 6 rows to keep the popup compact.
    const matches = useMemo(() => {
      if (!detection) return [];
      const q = detection.query.toLowerCase();
      const isSelfAlias = q.length > 0 && SELF_ALIASES.some((a) => a.startsWith(q));

      const self = currentUserId ? members.find((m) => m.id === currentUserId) : undefined;
      const startsWith: User[] = [];
      const contains: User[] = [];
      for (const m of members) {
        if (m.id === currentUserId) continue; // self pinned separately below
        const ln = m.name.toLowerCase();
        if (ln.startsWith(q)) startsWith.push(m);
        else if (q.length > 0 && ln.includes(q)) contains.push(m);
      }
      const ordered: User[] = [];
      if (self) {
        const selfNameMatches = self.name.toLowerCase().startsWith(q) || self.name.toLowerCase().includes(q);
        // Pin self to the top when the query is empty, when it's a
        // me/myself alias, or when the user's actual name matches.
        if (q.length === 0 || isSelfAlias || selfNameMatches) {
          ordered.push(self);
        }
      }
      ordered.push(...startsWith, ...contains);
      return ordered.slice(0, 6);
    }, [members, detection?.query, currentUserId]);

    // Open/close + reset highlight as the detection state flips. Using
    // a useEffect so we don't setState during render.
    useEffect(() => {
      if (detection && matches.length > 0) {
        setOpen(true);
        setActiveIndex(0);
      } else {
        setOpen(false);
      }
    }, [detection?.query, matches.length, detection?.tokenStart]);

    const accept = (member: User) => {
      if (!detection) return;
      const before = value.slice(0, detection.tokenStart);
      const after = value.slice(detection.tokenEnd);
      const needsSpace = !/^\s/.test(after);
      const insertion = `@${member.name}${needsSpace ? ' ' : ''}`;
      const next = before + insertion + after;
      onChange(next);
      setOpen(false);
      const newCaret = (before + insertion).length;
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.focus();
        ta.setSelectionRange(newCaret, newCaret);
      });
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (open && matches.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIndex((i) => (i + 1) % matches.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIndex((i) => (i - 1 + matches.length) % matches.length);
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          e.stopPropagation();
          const m = matches[activeIndex];
          if (m) accept(m);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setOpen(false);
          return;
        }
      }
      onKeyDown?.(e);
    };

    const handleScroll = (e: UIEvent<HTMLTextAreaElement>) => {
      // Propagate scroll to the overlay so the highlight rectangles
      // stay aligned with the visible text. Do this synchronously
      // (no rAF) — even one frame of drift is visible to the user.
      if (overlayRef.current) {
        overlayRef.current.scrollTop = e.currentTarget.scrollTop;
        overlayRef.current.scrollLeft = e.currentTarget.scrollLeft;
      }
      onScroll?.(e);
    };

    return (
      <div className={styles.wrapper}>
        <div
          ref={overlayRef}
          aria-hidden="true"
          className={cn(className, styles.overlay)}
        >
          {renderMentions(value, members, { mentionClassName: styles.mentionPill })}
          {/* Trailing newline guarantees the overlay's height matches
              the textarea even when value ends with "\n". Without this,
              a final newline would render as zero height in the div and
              the last highlight could clip if the user scrolls. */}
          {'\n'}
        </div>
        <Textarea
          {...rest}
          ref={textareaRef}
          value={value}
          className={cn(className, styles.textarea)}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onScroll={handleScroll}
          onBlur={(e) => {
            setOpen(false);
            onBlur?.(e);
          }}
        />
        {open && matches.length > 0 && (
          <div className={styles.popup} role="listbox" aria-label="Mention members">
            {matches.map((m, i) => (
              <button
                key={m.id}
                type="button"
                role="option"
                aria-selected={i === activeIndex}
                className={i === activeIndex ? `${styles.row} ${styles.rowActive}` : styles.row}
                onMouseDown={(e) => { e.preventDefault(); accept(m); }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <Avatar name={m.name} size="sm" src={m.avatarUrl} />
                <span className={styles.name}>
                  {m.name}
                  {m.id === currentUserId && <span className={styles.meTag}> (me)</span>}
                </span>
                <span className={styles.email}>{m.email}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);
